import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import {
  getEventTicketPaymentInfo,
  initiateEventTicketPayment,
  formatAmount,
} from '../services/eventTicketPaymentService';
import './EventTicketPayment.css';

// Firefox has issues with Paymob iframe 3DS redirects
const isFirefox = () => navigator.userAgent.toLowerCase().includes('firefox');

// Check if we're inside an iframe (widget)
const isInIframe = () => {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true; // If we can't access parent, we're likely in an iframe
  }
};

const EventTicketPayment = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [selectedTicketIds, setSelectedTicketIds] = useState([]);
  const [payerName, setPayerName] = useState('');
  const [payerEmail, setPayerEmail] = useState('');
  const [processing, setProcessing] = useState(false);
  const [showIframe, setShowIframe] = useState(false);
  const [iframeUrl, setIframeUrl] = useState('');

  // Check for success query param from redirect
  const successParam = searchParams.get('success');
  const fullyPaidParam = searchParams.get('fully_paid');

  // Check if user came from widget (via query param or iframe detection)
  const isFromWidget = searchParams.get('source') === 'widget' || isInIframe();

  // If we're inside an iframe and have a payment result, break out to parent
  useEffect(() => {
    if (successParam !== null && window.self !== window.top) {
      // We're inside the Paymob payment iframe after redirect — reload parent with result
      window.top.location.href = window.location.href;
      return;
    }
  }, [successParam]);

  useEffect(() => {
    fetchPaymentInfo();
  }, [token]);

  const fetchPaymentInfo = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getEventTicketPaymentInfo(token);
      setPaymentInfo(data);

      // Auto-select all approved (payable) tickets by default
      const approvedTicketIds = data.tickets
        .filter((t) => t.status === 'approved')
        .map((t) => t.id);
      setSelectedTicketIds(approvedTicketIds);
    } catch (err) {
      console.error('Failed to fetch payment info', err);
      setError(err.response?.data?.message || 'Invalid payment link or link has expired');
    } finally {
      setLoading(false);
    }
  };

  const handleTicketToggle = (ticketId) => {
    setSelectedTicketIds((prev) =>
      prev.includes(ticketId)
        ? prev.filter((id) => id !== ticketId)
        : [...prev, ticketId]
    );
  };

  const handleSelectAll = () => {
    const approvedTicketIds = paymentInfo.tickets
      .filter((t) => t.status === 'approved')
      .map((t) => t.id);
    setSelectedTicketIds(approvedTicketIds);
  };

  const handleDeselectAll = () => {
    setSelectedTicketIds([]);
  };

  const handlePayment = async () => {
    if (selectedTicketIds.length === 0) {
      setError('Please select at least one ticket to pay for');
      return;
    }

    if (!payerName.trim()) {
      setError('Please enter your name');
      return;
    }

    if (!payerEmail.trim()) {
      setError('Please enter your email');
      return;
    }

    try {
      setProcessing(true);
      setError(null);

      const response = await initiateEventTicketPayment(token, {
        ticketIds: selectedTicketIds,
        payerName: payerName.trim(),
        payerEmail: payerEmail.trim(),
      });

      // Firefox has issues with 3DS redirects in iframes - use full page redirect
      if (isFirefox()) {
        window.location.href = response.paymentUrl;
        return;
      }

      // Other browsers: use iframe
      setIframeUrl(response.paymentUrl);
      setShowIframe(true);
    } catch (err) {
      console.error('Failed to initiate payment', err);
      setError(err.response?.data?.message || 'Failed to initiate payment. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const calculateSelectedTotal = () => {
    if (!paymentInfo) return 0;
    return paymentInfo.tickets
      .filter((t) => selectedTicketIds.includes(t.id))
      .reduce((sum, t) => sum + t.priceCents, 0);
  };

  const calculateFee = (ticketSubtotalCents, ticketCount) => {
    if (!paymentInfo) return 0;
    const fixedFee = paymentInfo.fixedFeeCents || 0;
    const percentFee = paymentInfo.feePercent || 0;
    return fixedFee * ticketCount + Math.round(ticketSubtotalCents * percentFee / 100);
  };

  const calculateGrandTotal = () => {
    const subtotal = calculateSelectedTotal();
    return subtotal + calculateFee(subtotal, selectedTicketIds.length);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':');
    const h = parseInt(hours, 10);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const getStatusBadge = (ticket) => {
    if (ticket.isPaid || ticket.status === 'paid') {
      return <span className="badge bg-success">Paid</span>;
    }
    if (ticket.status === 'cancelled') {
      return <span className="badge bg-secondary">Cancelled</span>;
    }
    if (ticket.status === 'refunded') {
      return <span className="badge bg-warning text-dark">Refunded</span>;
    }
    if (ticket.status === 'approved') {
      return <span className="badge bg-info">Ready to Pay</span>;
    }
    return <span className="badge bg-warning text-dark">Pending Approval</span>;
  };

  if (loading) {
    return (
      <div className="event-payment-container">
        <div className="loading-state">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p>Loading payment information...</p>
        </div>
      </div>
    );
  }

  if (error && !paymentInfo) {
    return (
      <div className="event-payment-container">
        <div className="error-state">
          <i className="bi bi-exclamation-circle"></i>
          <h2>Payment Link Error</h2>
          <p>{error}</p>
          <button className="btn btn-primary" onClick={() => navigate('/')}>
            Go to Homepage
          </button>
        </div>
      </div>
    );
  }

  if (showIframe) {
    return (
      <div className="event-payment-container">
        <div className="iframe-container">
          <div className="iframe-header">
            <button
              className="btn btn-outline-secondary"
              onClick={() => {
                setShowIframe(false);
                fetchPaymentInfo(); // Refresh payment info
              }}
            >
              <i className="bi bi-arrow-left me-2"></i>
              Back
            </button>
            <h5>Complete Your Payment</h5>
          </div>
          <div className="payment-iframe-wrapper">
            <iframe
              src={iframeUrl}
              title="Paymob Payment"
              className="payment-iframe"
              allow="payment"
            />
          </div>
          <div className="iframe-footer">
            <div className="payment-secure-info">
              <i className="bi bi-shield-lock-fill"></i>
              Your payment is secured by Paymob
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Determine payment result states
  const isPaymentFailed = successParam === 'false';
  const isFullyPaid = paymentInfo?.isFullyPaid || fullyPaidParam === 'true';
  const isPartialPaymentSuccess = successParam === 'true' && fullyPaidParam === 'false' && !paymentInfo?.isFullyPaid;

  if (isPaymentFailed) {
    return (
      <div className="event-payment-container">
        <div className="failure-state">
          <div className="failure-icon-wrapper">
            <i className="bi bi-x-circle-fill"></i>
          </div>
          <h2>Payment Not Completed</h2>
          <p className="failure-subtitle">
            Your payment could not be processed. This could be due to:
          </p>
          <ul className="failure-reasons">
            <li>Insufficient funds</li>
            <li>Card declined by your bank</li>
            <li>Incorrect card details</li>
            <li>Network issues during payment</li>
          </ul>
          <button
            className="btn btn-primary btn-lg"
            onClick={() => {
              window.location.href = window.location.pathname;
            }}
          >
            <i className="bi bi-arrow-clockwise me-2"></i>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (isFullyPaid || isPartialPaymentSuccess) {
    const paidTicketsList = paymentInfo?.tickets?.filter((t) => t.isPaid || t.status === 'paid') || [];

    return (
      <div className="event-payment-container">
        <div className="success-state">
          <div className="success-icon-wrapper">
            <i className={`bi ${isPartialPaymentSuccess ? 'bi-check-circle' : 'bi-check-circle-fill'}`}></i>
          </div>
          <h2>{isPartialPaymentSuccess ? 'Payment Received!' : 'Payment Successful!'}</h2>
          <p className="success-subtitle">
            {isPartialPaymentSuccess
              ? 'Your payment has been processed. Your order still has a remaining balance. QR codes will be sent once all tickets are fully paid.'
              : 'Your tickets have been confirmed. Check your email for the QR codes.'}
          </p>

          {/* Event Details Card */}
          <div className="success-event-card">
            <div className="success-event-details">
              <h3>{paymentInfo?.eventName}</h3>
              <p className="success-restaurant">{paymentInfo?.restaurantName}</p>
              <div className="success-event-info">
                <div className="info-row">
                  <i className="bi bi-calendar-event"></i>
                  <span>{formatDate(paymentInfo?.eventDate)}</span>
                </div>
                <div className="info-row">
                  <i className="bi bi-clock"></i>
                  <span>{formatTime(paymentInfo?.eventStartTime)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Tickets Summary */}
          <div className="success-tickets-section">
            <h4>{isPartialPaymentSuccess ? `Paid Tickets (${paidTicketsList.length})` : `Your Tickets (${paidTicketsList.length})`}</h4>
            <div className="success-tickets-list">
              {paidTicketsList.map((ticket) => (
                <div key={ticket.id} className="success-ticket-item">
                  <div className="ticket-holder-info">
                    <i className="bi bi-person-check"></i>
                    <span>{ticket.firstName} {ticket.lastName}</span>
                  </div>
                  <div className="ticket-type-info">
                    {ticket.ticketTypeName}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Order Summary */}
          <div className="success-order-summary">
            <div className="summary-row">
              <span>Order #</span>
              <span>{paymentInfo?.orderId}</span>
            </div>
            <div className="summary-row">
              <span>Total Paid</span>
              <span className="total-amount">EGP {formatAmount(paymentInfo?.paidAmountCents || paymentInfo?.totalCents || 0)}</span>
            </div>
            {isPartialPaymentSuccess && paymentInfo?.totalCents && (
              <div className="summary-row">
                <span>Remaining</span>
                <span className="remaining-amount">EGP {formatAmount((paymentInfo.totalCents || 0) - (paymentInfo.paidAmountCents || 0))}</span>
              </div>
            )}
          </div>

          {/* QR Code Notice or Partial Payment Notice */}
          {isPartialPaymentSuccess ? (
            <div className="qr-notice partial-notice">
              <i className="bi bi-info-circle"></i>
              <p>QR codes will be sent to <strong>{paymentInfo?.buyerEmail || 'your email'}</strong> once all tickets are paid.</p>
            </div>
          ) : (
            <div className="qr-notice">
              <i className="bi bi-envelope-check"></i>
              <p>QR codes have been sent to <strong>{paymentInfo?.buyerEmail || 'your email'}</strong></p>
            </div>
          )}


          {/* Back Button - depends on source */}
          {isPartialPaymentSuccess && paymentInfo?.orderId ? (
            <button
              className="btn btn-primary btn-lg btn-back-restaurant"
              onClick={() => {
                // Reload the payment page to show remaining tickets
                window.location.href = window.location.pathname;
              }}
            >
              <i className="bi bi-credit-card me-2"></i>
              Pay Remaining Tickets
            </button>
          ) : isFromWidget && paymentInfo?.restaurantId ? (
            <button
              className="btn btn-primary btn-lg btn-back-restaurant"
              onClick={() => navigate(`/widget?restaurantId=${paymentInfo.restaurantId}`)}
            >
              <i className="bi bi-arrow-left me-2"></i>
              Make Another Reservation
            </button>
          ) : paymentInfo?.restaurantId ? (
            <button
              className="btn btn-primary btn-lg btn-back-restaurant"
              onClick={() => navigate(`/restaurant/${paymentInfo.restaurantId}`)}
            >
              <i className="bi bi-arrow-left me-2"></i>
              Back to {paymentInfo?.restaurantName || 'Restaurant'}
            </button>
          ) : (
            <button
              className="btn btn-primary btn-lg btn-back-restaurant"
              onClick={() => navigate('/')}
            >
              <i className="bi bi-house me-2"></i>
              Explore More Events
            </button>
          )}
        </div>
      </div>
    );
  }

  // Approved tickets can be paid
  const approvedTickets = paymentInfo?.tickets?.filter((t) => t.status === 'approved') || [];
  // Paid tickets
  const paidTickets = paymentInfo?.tickets?.filter((t) => t.isPaid || t.status === 'paid') || [];
  // Pending tickets (waiting for admin approval)
  const pendingTickets = paymentInfo?.tickets?.filter((t) => t.status === 'pending') || [];

  return (
    <div className="event-payment-container">
      <div className="payment-card">
        {/* Header */}
        <div className="payment-header">
          <h1>{paymentInfo?.eventName}</h1>
          <p className="restaurant-name">{paymentInfo?.restaurantName}</p>
        </div>

        {/* Event Details */}
        <div className="event-details">
          <div className="detail-item">
            <i className="bi bi-calendar-event"></i>
            <span>{formatDate(paymentInfo?.eventDate)}</span>
          </div>
          <div className="detail-item">
            <i className="bi bi-clock"></i>
            <span>{formatTime(paymentInfo?.eventStartTime)}</span>
          </div>
          {paymentInfo?.eventDescription && (
            <div className="detail-item">
              <i className="bi bi-info-circle"></i>
              <span>{paymentInfo.eventDescription}</span>
            </div>
          )}
        </div>

        {/* Payment Progress */}
        <div className="payment-progress">
          <h4>Payment Progress</h4>
          <div className="progress-bar-container">
            <div
              className="progress-bar-fill"
              style={{
                width: `${((paymentInfo?.paidAmountCents || 0) / (paymentInfo?.totalCents || 1)) * 100}%`,
              }}
            />
          </div>
          <div className="progress-stats">
            <span>
              <strong>{paidTickets.length}</strong> of{' '}
              <strong>{paymentInfo?.tickets?.length || 0}</strong> tickets paid
            </span>
            <span>
              EGP {formatAmount(paymentInfo?.paidAmountCents || 0)} / EGP{' '}
              {formatAmount(paymentInfo?.totalCents || 0)}
            </span>
          </div>
        </div>

        {/* Order Info */}
        <div className="order-info">
          <p>
            <strong>Order #{paymentInfo?.orderId}</strong> for {paymentInfo?.buyerName}
          </p>
        </div>

        {/* Message if any */}
        {paymentInfo?.message && (
          <div className="payment-message">
            <i className="bi bi-info-circle"></i>
            <span>{paymentInfo.message}</span>
          </div>
        )}

        {/* Paid Tickets Section */}
        {paidTickets.length > 0 && (
          <div className="tickets-section paid-tickets">
            <h4>Paid Tickets</h4>
            <div className="tickets-list">
              {paidTickets.map((ticket) => (
                <div key={ticket.id} className="ticket-item paid">
                  <div className="ticket-info">
                    <div className="ticket-holder">
                      <i className="bi bi-person-check"></i>
                      <span>{ticket.firstName} {ticket.lastName}</span>
                    </div>
                    <div className="ticket-type">{ticket.ticketTypeName}</div>
                    {ticket.payerName && (
                      <div className="ticket-payer">
                        Paid by: {ticket.payerName}
                      </div>
                    )}
                  </div>
                  <div className="ticket-right">
                    <span className="ticket-price">EGP {formatAmount(ticket.priceCents)}</span>
                    {getStatusBadge(ticket)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pending Tickets Section (waiting for approval) */}
        {pendingTickets.length > 0 && (
          <div className="tickets-section pending-tickets">
            <h4>Pending Approval</h4>
            <p className="pending-notice">
              <i className="bi bi-hourglass-split me-2"></i>
              These tickets are waiting for admin approval before payment can be made.
            </p>
            <div className="tickets-list">
              {pendingTickets.map((ticket) => (
                <div key={ticket.id} className="ticket-item pending">
                  <div className="ticket-info">
                    <div className="ticket-holder">
                      <i className="bi bi-person"></i>
                      <span>{ticket.firstName} {ticket.lastName}</span>
                    </div>
                    <div className="ticket-type">{ticket.ticketTypeName}</div>
                  </div>
                  <div className="ticket-right">
                    <span className="ticket-price">EGP {formatAmount(ticket.priceCents)}</span>
                    <span className="badge bg-warning">Pending</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Approved Tickets Section (ready for payment) */}
        {approvedTickets.length > 0 && (
          <div className="tickets-section approved-tickets">
            <div className="section-header">
              <h4>Select Tickets to Pay</h4>
              <div className="selection-controls">
                <button
                  type="button"
                  className="btn btn-link btn-sm"
                  onClick={handleSelectAll}
                >
                  Select All
                </button>
                <span>|</span>
                <button
                  type="button"
                  className="btn btn-link btn-sm"
                  onClick={handleDeselectAll}
                >
                  Clear
                </button>
              </div>
            </div>
            <div className="tickets-list">
              {approvedTickets.map((ticket) => (
                <div
                  key={ticket.id}
                  className={`ticket-item selectable ${selectedTicketIds.includes(ticket.id) ? 'selected' : ''}`}
                  onClick={() => handleTicketToggle(ticket.id)}
                >
                  <div className="ticket-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedTicketIds.includes(ticket.id)}
                      onChange={() => handleTicketToggle(ticket.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  <div className="ticket-info">
                    <div className="ticket-holder">
                      <i className="bi bi-person"></i>
                      <span>{ticket.firstName} {ticket.lastName}</span>
                    </div>
                    <div className="ticket-type">{ticket.ticketTypeName}</div>
                  </div>
                  <div className="ticket-right">
                    <span className="ticket-price">EGP {formatAmount(ticket.priceCents)}</span>
                    <span className="badge bg-info">Ready to Pay</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Payment Form */}
            <div className="pay-form">
              {error && (
                <div className="alert alert-danger">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  {error}
                </div>
              )}

              {/* Payer Info */}
              <div className="payer-info">
                <div className="form-group">
                  <label>Your Name <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control"
                    value={payerName}
                    onChange={(e) => setPayerName(e.target.value)}
                    placeholder="Enter your name"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Your Email <span className="text-danger">*</span></label>
                  <input
                    type="email"
                    className="form-control"
                    value={payerEmail}
                    onChange={(e) => setPayerEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                  />
                </div>
              </div>

              {/* Total */}
              <div className="total-section">
                <div className="total-breakdown">
                  <span>
                    {selectedTicketIds.length} ticket{selectedTicketIds.length !== 1 ? 's' : ''} selected
                  </span>
                </div>
                <div className="total-amount">
                  <span>Subtotal:</span>
                  <span className="amount">EGP {formatAmount(calculateSelectedTotal())}</span>
                </div>
                {calculateFee(calculateSelectedTotal(), selectedTicketIds.length) > 0 && (
                  <div className="total-amount fee-line">
                    <span>Service Fee:</span>
                    <span className="amount">EGP {formatAmount(calculateFee(calculateSelectedTotal(), selectedTicketIds.length))}</span>
                  </div>
                )}
                {calculateFee(calculateSelectedTotal(), selectedTicketIds.length) > 0 && (
                  <div className="total-amount grand-total">
                    <span><strong>Total:</strong></span>
                    <span className="amount"><strong>EGP {formatAmount(calculateGrandTotal())}</strong></span>
                  </div>
                )}
              </div>

              {/* Pay Button */}
              <button
                className="btn btn-primary btn-pay"
                onClick={handlePayment}
                disabled={processing || selectedTicketIds.length === 0}
              >
                {processing ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Processing...
                  </>
                ) : (
                  <>
                    <i className="bi bi-credit-card me-2"></i>
                    Pay EGP {formatAmount(calculateGrandTotal())}
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventTicketPayment;
